import React, { useState, useEffect } from "react";
import {
  Trash2,
  X,
  AlertCircle,
  CheckCircle,
  Package,
  FileText,
  Megaphone,
  Settings,
  Users,
  MessageSquare,
  Activity,
  Workflow,
  Shield,
  FolderOpen,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { useChat } from "../contexts/ChatContext";
import CustomSelect from "./CustomSelect";
import { productsApi } from "../services/productsApi";
import { useModal } from "../hooks/useModal";

const CacheManagement = () => {
  const { translate } = useLanguage();
  const { isEffectivelyAdmin, isPlatformOnlySuperAdmin } = useAuth();
  const { chats, selectedChatId, setSelectedChatId, loading: chatsLoading, chatsLoaded, loadChats } = useChat();
  const [state, setState] = useState({
    loading: {
      products: false,
      generalInformation: false,
      campaigns: false,
      internalProcedures: false,
      tenantConfig: false,
      agentCache: false,
      chatHistory: false,
      ingestionStatus: false,
      workflow: false,
      allTechnical: false,
    },
    error: null,
    success: null,
    confirmDialog: null,
  });

  // Helper function to update state
  const updateState = (updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  // Load chats when component mounts (only for admins)
  useEffect(() => {
    if (!isPlatformOnlySuperAdmin() && !chatsLoaded && !chatsLoading) {
      loadChats(); // Backend will get tenant_id from JWT
    }
  }, [isPlatformOnlySuperAdmin, chatsLoaded, chatsLoading, loadChats]);

  // StatusMessage component
  const StatusMessage = ({ error, success, onClose }) => {
    if (!error && !success) return null;

    return (
      <div
        className="p-4 rounded-xl border"
        style={
          error
            ? { backgroundColor: "var(--color-error-muted)", border: "1px solid var(--color-error-border)", color: "var(--color-error)" }
            : { backgroundColor: "var(--color-mint-muted)", borderColor: "var(--color-mint-border)" }
        }
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            {error ? (
              <AlertCircle className="h-5 w-5 mr-2" style={{ color: "var(--color-error)" }} />
            ) : (
              <CheckCircle className="h-5 w-5 mr-2" style={{ color: "var(--color-mint)" }} />
            )}
            <span
              className="text-sm font-medium"
              style={error ? { color: "var(--color-error)" } : { color: "var(--color-mint-dark)" }}
            >
              {error || success}
            </span>
          </div>
          <button
            onClick={onClose}
            className="transition-colors" style={{ color: "var(--color-text-muted)" }}
            onMouseEnter={(e) => { e.currentTarget.style.color = "var(--color-text-secondary)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = "var(--color-text-muted)"; }}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  const handleClearSpecificCache = (cacheType, apiFunction, cacheKey) => {
    updateState({
      confirmDialog: {
        cacheType,
        apiFunction,
        cacheKey,
        message:
          translate("confirmClearCacheMessage")?.replace(
            "{cacheType}",
            cacheType
          ) || `Are you sure you want to clear the ${cacheType} cache?`,
      },
    });
  };

  const handleCloseConfirmDialog = () => {
    updateState({ confirmDialog: null });
  };

  const confirmDialogRef = useModal(
    !!state.confirmDialog,
    handleCloseConfirmDialog
  );

  const confirmClearCache = async () => {
    const { cacheType, apiFunction, cacheKey } = state.confirmDialog;

    try {
      updateState({
        loading: { ...state.loading, [cacheKey]: true },
        error: null,
        success: null,
        confirmDialog: null,
      });

      // Pass selectedChatId to API function
      const response = await apiFunction(selectedChatId);

      // Handle different response formats (single pattern vs multiple patterns)
      let successMessage;
      if (response.message && response.results) {
        // Multiple patterns response (like products cache)
        successMessage = `${cacheType} cache cleared successfully!`;
      } else {
        // Single pattern response
        successMessage =
          translate("cacheClearedSuccess")?.replace("{cacheType}", cacheType) ||
          `${cacheType} cache cleared successfully!`;
      }

      updateState({
        success: successMessage,
      });
    } catch (err) {
      const errorMessage =
        err.response?.data?.detail ||
        err.message ||
        translate("failedToClearCache") ||
        "Failed to clear cache";

      updateState({ error: errorMessage });
    } finally {
      updateState({
        loading: { ...state.loading, [cacheKey]: false },
      });
    }
  };

  const cancelClearCache = () => {
    handleCloseConfirmDialog();
  };

  const clearMessages = () => {
    updateState({ error: null, success: null });
  };

  // Confirmation Dialog Component
  const ConfirmationDialog = () => {
    if (!state.confirmDialog) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div
          ref={confirmDialogRef}
          className="card rounded-xl border border-[var(--color-border)] p-6 max-w-md w-full mx-4"
        >
          <div className="flex items-center mb-4">
            <AlertCircle className="h-6 w-6 mr-3" style={{ color: "var(--color-warning)" }} />
            <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
              {translate("confirmClearCache") || "Confirm Clear"}
            </h3>
          </div>
          <p className="text-[var(--color-text-secondary)] mb-6">{state.confirmDialog.message}</p>
          <div className="flex justify-end space-x-3">
            <button
              onClick={cancelClearCache}
              disabled={state.loading[state.confirmDialog?.cacheKey] || false}
              className="btn-secondary disabled:opacity-50"
            >
              {translate("cancel") || "Cancel"}
            </button>
            <button
              onClick={confirmClearCache}
              disabled={state.loading[state.confirmDialog?.cacheKey] || false}
              className="btn-destructive disabled:opacity-50 flex items-center"
            >
              {state.loading[state.confirmDialog?.cacheKey] ? (
                <div style={{ width: '16px', height: '16px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 8 }} />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {translate("confirmClearCache") || "Confirm Clear"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  /* ─── Wait for chats to be ready before rendering ─── */
  const isChatsReady = isPlatformOnlySuperAdmin() || (chatsLoaded && !chatsLoading);
  if (!isPlatformOnlySuperAdmin() && !isChatsReady) {
    return (
      <div className="space-y-6 animate-pulse">
        <div>
          <div className="h-7 w-52 rounded-lg mb-2 bg-[var(--color-skeleton)]" />
          <div className="h-4 w-80 rounded bg-[var(--color-skeleton-light)]" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <div key={i} className="h-32 rounded-xl bg-[var(--color-skeleton-light)]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Chat Selector */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
            {translate("cacheManagement") || "Cache Management"}
          </h1>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            {translate("cacheManagementDescription") ||
              "Manage Redis cache for your tenant"}
          </p>
        </div>

        {/* Chat Selector - Only show if user has access to chats and more than 1 active chat */}
        {!isPlatformOnlySuperAdmin() && chats && chats.filter(chat => chat.is_active).length > 1 && (
          <CustomSelect
            value={selectedChatId || ""}
            onChange={(val) => setSelectedChatId(val)}
            label={translate("selectChat") || "Select Chat"}
            icon={MessageSquare}
            disabled={chatsLoading}
            className="w-56"
            options={chats
              .filter((chat) => chat.is_active)
              .sort((a, b) => (a.chatwoot_inbox_id || 0) - (b.chatwoot_inbox_id || 0))
              .map((chat) => ({ value: chat.id, label: chat.name }))
            }
          />
        )}
      </div>

      {/* Chat info banner - Only show if more than 1 active chat */}
      {selectedChatId && chats.filter(chat => chat.is_active).length > 1 && (
        <div className="border rounded-xl p-3" style={{ backgroundColor: "var(--color-mint-muted)", borderColor: "var(--color-mint-border)" }}>
          <p className="text-sm text-[var(--color-text-secondary)]">
            <strong>{translate("clearingCacheFor") || "A limpar cache para"}:</strong>{" "}
            {chats.find(c => c.id === selectedChatId)?.name || "Chat selecionado"}
          </p>
        </div>
      )}

      {/* Status Messages */}
      <StatusMessage
        error={state.error}
        success={state.success}
        onClose={clearMessages}
      />

      <div className="space-y-6">
      {/* Cache Actions - Single card */}
      <div className="card rounded-xl border border-[var(--color-border)] p-6">
        <div className="mb-6">
          <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
            {translate("cacheActions") || "Cache Actions"}
          </h3>
          <p className="text-[var(--color-text-muted)] text-xs mt-1">
            {translate("cacheActionsDescription") ||
              "Clear cache by specific content type"}
          </p>
        </div>

        <div className="divide-y divide-[var(--color-border)]">
          {/* Clear Products Cache */}
          <div className="flex items-center justify-between py-4 first:pt-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                <Package className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                  {translate("clearProductsCache") || "Products Cache"}
                </h4>
                <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                  {translate("clearProductsCacheDescription") || "Clear all product-related cache"}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleClearSpecificCache("Products", productsApi.clearProductsCache, "products")}
              disabled={state.loading.products}
              className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
            >
              {state.loading.products ? (
                <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              {state.loading.products ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
            </button>
          </div>

          {/* Clear General Information Cache */}
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                <FileText className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                  {translate("clearGeneralInformationCache") || "General Information Cache"}
                </h4>
                <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                  {translate("clearGeneralInformationCacheDescription") || "Clear general information-specific cache"}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleClearSpecificCache("General Information", productsApi.clearGeneralInformationCache, "generalInformation")}
              disabled={state.loading.generalInformation}
              className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
            >
              {state.loading.generalInformation ? (
                <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              {state.loading.generalInformation ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
            </button>
          </div>

          {/* Clear Campaigns Cache */}
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                <Megaphone className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
              </div>
              <div className="min-w-0">
                <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                  {translate("clearCampaignsCache") || "Campaigns Cache"}
                </h4>
                <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                  {translate("clearCampaignsCacheDescription") || "Clear campaigns-specific cache"}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleClearSpecificCache("Campaigns", productsApi.clearCampaignsCache, "campaigns")}
              disabled={state.loading.campaigns}
              className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
            >
              {state.loading.campaigns ? (
                <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
              ) : (
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              )}
              {state.loading.campaigns ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
            </button>
          </div>

          {/* Clear Internal Procedures Cache - Only show for internal chats */}
          {(() => {
            const selectedChat = chats.find(c => c.id === selectedChatId);
            if (!selectedChat?.internal_chat) return null;
            return (
              <div className="flex items-center justify-between py-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                    <FolderOpen className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                  </div>
                  <div className="min-w-0">
                    <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                      {translate("clearInternalProceduresCache") || "Internal Procedures Cache"}
                    </h4>
                    <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                      {translate("clearInternalProceduresCacheDescription") || "Clear internal procedures-specific cache"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleClearSpecificCache("Internal Procedures", productsApi.clearInternalProceduresCache, "internalProcedures")}
                  disabled={state.loading.internalProcedures}
                  className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
                >
                  {state.loading.internalProcedures ? (
                    <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                  )}
                  {state.loading.internalProcedures ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
                </button>
              </div>
            );
          })()}
        </div>
      </div>

      {/* Advanced Cache Management (Admins Only) */}
      {isEffectivelyAdmin() && (
        <div className="card rounded-xl border border-[var(--color-border)] p-6">
          <div className="mb-6">
            <div className="flex items-center mb-2">
              <Shield className="h-6 w-6 mr-2" style={{ color: "var(--color-mint)" }} />
              <h3 className="font-semibold text-base text-[var(--color-text-primary)]">
                {translate("advancedCacheManagement") ||
                  "Advanced Cache Management"}
              </h3>
            </div>
            <p className="text-[var(--color-text-muted)] text-xs">
              {translate("advancedCacheManagementDescription") ||
                "Clear technical system caches - use with caution"}
            </p>
          </div>

          <div className="divide-y divide-[var(--color-border)]">
            {/* Clear Tenant Config Cache */}
            <div className="flex items-center justify-between py-4 first:pt-0">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                  <Settings className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {translate("clearTenantConfigCache") || "Tenant Config Cache"}
                  </h4>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("clearTenantConfigCacheDescription") || "Clear tenant configuration cache"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClearSpecificCache("Tenant Config", productsApi.clearTenantConfigCache, "tenantConfig")}
                disabled={state.loading.tenantConfig}
                className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
              >
                {state.loading.tenantConfig ? (
                  <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                {state.loading.tenantConfig ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
              </button>
            </div>

            {/* Clear Agent Cache */}
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                  <Users className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {translate("clearAgentCache") || "Agent Cache"}
                  </h4>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("clearAgentCacheDescription") || "Clear agent and agent metadata cache"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClearSpecificCache("Agent Cache", productsApi.clearAgentCache, "agentCache")}
                disabled={state.loading.agentCache}
                className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
              >
                {state.loading.agentCache ? (
                  <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                {state.loading.agentCache ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
              </button>
            </div>

            {/* Clear Chat History Cache */}
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                  <MessageSquare className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {translate("clearChatHistoryCache") || "Chat History Cache"}
                  </h4>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("clearChatHistoryCacheDescription") || "Clear all chat history"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClearSpecificCache("Chat History", productsApi.clearChatHistoryCache, "chatHistory")}
                disabled={state.loading.chatHistory}
                className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
              >
                {state.loading.chatHistory ? (
                  <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                {state.loading.chatHistory ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
              </button>
            </div>

            {/* Clear Ingestion Status Cache */}
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                  <Activity className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {translate("clearIngestionStatusCache") || "Ingestion Status Cache"}
                  </h4>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("clearIngestionStatusCacheDescription") || "Clear ingestion status cache"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClearSpecificCache("Ingestion Status", productsApi.clearIngestionStatusCache, "ingestionStatus")}
                disabled={state.loading.ingestionStatus}
                className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
              >
                {state.loading.ingestionStatus ? (
                  <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                {state.loading.ingestionStatus ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
              </button>
            </div>

            {/* Clear Workflow Cache */}
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-mint-muted)" }}>
                  <Workflow className="h-4 w-4" style={{ color: "var(--color-mint)" }} />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {translate("clearWorkflowCache") || "Workflow Cache"}
                  </h4>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("clearWorkflowCacheDescription") || "Clear workflow configuration cache"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClearSpecificCache("Workflow", productsApi.clearWorkflowCache, "workflow")}
                disabled={state.loading.workflow}
                className="btn-secondary disabled:opacity-50 flex-shrink-0 ml-4"
              >
                {state.loading.workflow ? (
                  <div style={{ width: '14px', height: '14px', border: '2px solid var(--color-mint)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                {state.loading.workflow ? translate("clearButtonLoading") || "Clearing..." : translate("clearButton") || "Clear"}
              </button>
            </div>

            {/* Clear All Technical Cache */}
            <div className="flex items-center justify-between py-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "var(--color-error-muted, rgba(239,68,68,0.08))" }}>
                  <AlertCircle className="h-4 w-4" style={{ color: "var(--color-error)" }} />
                </div>
                <div className="min-w-0">
                  <h4 className="text-sm font-medium" style={{ color: "var(--color-text-primary)" }}>
                    {translate("clearAllTechnicalCache") || "Clear All Technical Caches"}
                  </h4>
                  <p className="text-xs" style={{ color: "var(--color-text-muted)" }}>
                    {translate("clearAllTechnicalCacheDescription") || "Clear all technical caches at once"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => handleClearSpecificCache("All Technical Caches", productsApi.clearAllTechnicalCache, "allTechnical")}
                disabled={state.loading.allTechnical}
                className="btn-destructive disabled:opacity-50 flex-shrink-0 ml-4"
              >
                {state.loading.allTechnical ? (
                  <div style={{ width: '14px', height: '14px', border: '2px solid rgba(239,68,68,0.4)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginRight: 6 }} />
                ) : (
                  <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                )}
                {state.loading.allTechnical ? translate("clearButtonLoading") || "Clearing..." : translate("clearAllButton") || "Clear All"}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmationDialog />
    </div>
  );
};

export default CacheManagement;
